import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@project-stories/elements/Button/Button';
import { TextField } from "@src/stories/elements/TextField/TextField";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import shopify from 'frontend/js/shopify';
import './Popup.scss';

interface PopupProps {
  settings: any;
  blocks: any[];
}

export function Popup({ 
  settings, 
  blocks 
}: PopupProps) {
  // Form steps: email, sms, thank_you
  const [currentStep, setCurrentStep] = useState('email');
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { mobile_breakpoint } = settings;
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    phone: '',
    acceptTerms: false
  });
  const [isCopied, setIsCopied] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const isMobile = useMobileBreakpoint(mobile_breakpoint);
  const {
    layout_type,
    show_on_page_load,
    show_delay,
    show_once,
    cookie_expiry,
    overlay_opacity,
    animation_type,
    close_on_overlay_click,
    cookie_name,
    background_transparency = 0,
    close_button_position = 'right'
  } = settings?.section || {};

  // Group blocks by type for easier access
  const headerBlock = blocks.find(block => block.type === 'header');
  const imageBlock = blocks.find(block => block.type === 'image');
  const formBlock = blocks.find(block => block.type === 'form');
  const disclaimerBlock = blocks.find(block => block.type === 'disclaimer');
  const thankYouBlock = blocks.find(block => block.type === 'thank_you');

  const cookieKey = cookie_name || 'popup_shown';

  // Function to handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Function to handle code copying
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // Show copied state
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy code: ', err);
      });
  };

  // Function to close the popup
  const closePopup = () => {
    setIsOpen(false);
    setCurrentStep('email'); // Reset to first step when closed
    document.body.classList.remove('popup-active');
  };

  // Function to handle click outside popup
  const handleClickOutside = (e: MouseEvent) => {
    if (close_on_overlay_click && popupRef.current && !popupRef.current.contains(e.target as Node)) {
      closePopup();
    }
  };

useEffect(() => {
  let originalMetaViewport = null;
  
  if (isOpen) {

    originalMetaViewport = document.querySelector('meta[name="viewport"]')?.getAttribute('content');
  
    const metaTag = document.querySelector('meta[name="viewport"]');
    if (metaTag) {
      metaTag.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    } else {
      const newMetaTag = document.createElement('meta');
      newMetaTag.name = 'viewport';
      newMetaTag.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
      document.head.appendChild(newMetaTag);
    }
  }
  
  return () => {
    if (originalMetaViewport && isOpen) {
      const metaTag = document.querySelector('meta[name="viewport"]');
      if (metaTag) {
        metaTag.setAttribute('content', originalMetaViewport);
      }
    }
  };
}, [isOpen]);

  // Function to handle Escape key press
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closePopup();
    }
  };

  const handleKlaviyoSubmit = async (e: React.FormEvent, step: 'email' | 'sms' | 'thankyou') => {
            e.preventDefault();
  
    switch (currentStep) {
      case 'email':
        if (formData.email && (!formBlock?.settings?.show_name_field || formData.firstName)) {
          try {
            const response = await shopify.callKlaviyoSubscribe({
              email: formData.email,
              firstName: formData.firstName,
              listId: settings?.globals?.klaviyo_list_id,
              klaviyoAPIKey: settings?.globals?.klaviyo_public_api_key
            });
  
            if (response && response.ok) {
              setCurrentStep('sms');
              setError('');
            }  else {
     
              const errorData = await response?.json();
              if (errorData && errorData?.errors?.length > 0) {
  
                setError(errorData.errors[0]?.detail || 'Failed to submit subscription');
                
              } else {
                setError('Failed to submit subscription');
              }
            }
          } catch (err) {
            console.error('Klaviyo subscription error:', err);
            setError('An error occurred while processing your subscription');
          }
        }
        break;
  
      case 'sms':
        if (formData.email && (!formBlock?.settings?.show_name_field || formData.firstName)) {
          try {
            const response = await shopify.callKlaviyoSubscribe({
              email: formData.email,
              firstName: formData.firstName,
              phone: formData.phone,
              listId: settings?.globals?.klaviyo_list_id,
              klaviyoAPIKey: settings?.globals?.klaviyo_public_api_key
            });
            
            if (response && response.ok) {
              setCurrentStep('thank_you');
              setError('');
            } else {
     
              const errorData = await response?.json();
              if (errorData && errorData?.errors?.length > 0) {
  
                setError(errorData.errors[0]?.detail || 'Failed to submit subscription');
                
              } else {
                setError('Failed to submit subscription');
              }
            }
          } catch (err) {
            console.error('Klaviyo SMS subscription error:', err);
            setError('An error occurred while processing your subscription');
          }
        }
        break;
  
      case 'thank_you':
        break;
  
      default:
        break;
    }
  };

  const handleSkipSms = () => {
    setCurrentStep('thank_you');
  };

  // Set cookie to track if popup has been shown
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "; expires=" + date.toUTCString();
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  };

  // Get cookie value
  const getCookie = (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Show the popup after delay if configured to show on page load
  useEffect(() => {
    if (show_on_page_load) {
      // Check if popup should only be shown once and if it has been shown already
      if (show_once && getCookie(cookieKey)) {
        return; // Don't show if it should only be shown once and has been shown
      }

      const delay = show_delay ? show_delay * 1000 : 0;
      const timer = setTimeout(() => {
        setIsOpen(true);
        document.body.classList.add('popup-active');
        
        // If show_once is true, set a cookie to track that popup has been shown
        if (show_once) {
          setCookie(cookieKey, 'true', cookie_expiry || 7);
        }
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [show_on_page_load, show_delay, show_once, cookieKey, cookie_expiry]);

  // Add event listeners for click outside and escape key
  // useEffect(() => {
  //   if (isOpen) {
  //     document.addEventListener('mousedown', handleClickOutside);
  //     document.addEventListener('keydown', handleKeyDown);
  //   }

  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //     document.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [isOpen, close_on_overlay_click]);

  // Determine popup layout classes
  const getPopupLayoutClasses = () => {
    const baseClasses = 'popup-content relative bg-white rounded-[16px] lg:rounded-[32px] z-50 shadow-lg overflow-hidden';
    
    if (layout_type === 'fullscreen') {
      return `${baseClasses} w-screen h-screen max-w-none`;
    }
    return `${baseClasses} max-w-[90vw] lg:max-w-[70vw] mx-4 sm:mx-auto`;
  };

  // Determine animation classes
  const getAnimationClasses = () => {
    if (!animation_type) return '';
    
    switch (animation_type) {
      case 'fade':
        return 'animate-fade-in';
      case 'slide-up':
        return 'animate-slide-up';
      case 'slide-down':
        return 'animate-slide-down';
      case 'zoom':
        return 'animate-zoom-in';
      default:
        return '';
    }
  };

  // Apply background transparency
  const getBackgroundStyle = () => {
    if (background_transparency > 0) {
      const alpha = 1 - background_transparency;
      return { backgroundColor: `rgba(255, 255, 255, ${alpha})` };
    }
    return {};
  };

  // Don't render anything if popup is closed
  if (!isOpen) {
    return null;
  }

  // Render header block content
  const renderHeaderBlock = (step = 'email') => {
    if (!headerBlock) return null;
    
    const { 
      logo_image,
      logo_width,
      logo_alt,
      heading_email,
      heading_sms,
      heading_thank_you,
      heading_font_style,
      subheading_email,
      subheading_sms,
      subheading_thank_you,
      subheading_font_style,
      content_email,
      content_sms,
      content_thank_you,
      content_font_style,
      content_alignment
    } = headerBlock.settings || {};

    return (
      <div className={`popup-header text-${content_alignment || 'center'} ${currentStep == 'sms' ? 'mb-[40px] lg:mb-[24px]' : 'mb-[40px] lg:mb-[44px]' }`}>
        {logo_image && (
          <img 
            src={logo_image} 
            alt={logo_alt || 'Logo'} 
            className="popup-logo mx-auto mb-4"
            style={{ maxWidth: `${logo_width || 137}px` }}
          />
        )}
        
        {currentStep === 'email' && (
          <>
            {heading_email && (
              <div className={`${heading_font_style || 'text-2xl font-bold mb-2'} max-w-[350px] mx-auto text-center`}>
                {heading_email}
              </div>
            )}
            {subheading_email && (
              <div className={`popup-subheading ${subheading_font_style || ''} mb-2`}>
                <div dangerouslySetInnerHTML={{ __html: subheading_email }} />
              </div>
            )}
            {content_email && (
              <div className={`popup-content ${content_font_style || ''}`}>
                <div dangerouslySetInnerHTML={{ __html: content_email }} />
              </div>
            )}
          </>
        )}

        {currentStep === 'sms' && (
          <>
            {heading_sms && (
              <div className={heading_font_style || 'text-2xl font-bold mb-2'}>
                {heading_sms}
              </div>
            )}
            {subheading_sms && (
              <div className={`popup-subheading ${subheading_font_style || ''}  mb-2`}>
                <div dangerouslySetInnerHTML={{ __html: subheading_sms }} />
              </div>
            )}
            {content_sms && (
              <div className={`popup-content ${content_font_style || ''}`}>
                <div dangerouslySetInnerHTML={{ __html: content_sms }} />
              </div>
            )}
          </>
        )}

        {currentStep === 'thank_you' && (
          <>
            {heading_thank_you && (
              <div className={heading_font_style || 'text-2xl font-bold mb-2'}>
                {heading_thank_you}
              </div>
            )}
            {subheading_thank_you && (
              <div className={`popup-subheading ${subheading_font_style || ''}  mb-2 pt-2`}>
                <div dangerouslySetInnerHTML={{ __html: subheading_thank_you }} />
              </div>
            )}
            {content_thank_you && (
              <div className={`popup-content ${content_font_style || ''}`}>
                <div dangerouslySetInnerHTML={{ __html: content_thank_you }} />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render image block for mobile (top position)
  const renderMobileImageBlock = () => {
    if (!imageBlock) return null;

    const { 
      mobile_image,
      alt_text,
    } = imageBlock.settings || {};
    
    if (!mobile_image) return null;
    
    return (
      <div className="popup-image-container md:hidden">
        <img 
          src={mobile_image} 
          alt={alt_text || 'Popup Image'} 
          className="w-full h-auto object-cover popup-image"
        />
        <div className="wave-divider">
          <svg viewBox="0 0 100 20" preserveAspectRatio="none" fill="white" className="w-full h-8">
            <path d="M0,0 C50,20 80,20 100,0 L100,20 L0,20 Z" />
          </svg>
        </div>
      </div>
    );
  };

  // Render image block for desktop (right position)
  const renderDesktopImageBlock = () => {
    if (!imageBlock) return null;

    const { 
      image,
      alt_text,
    } = imageBlock.settings || {};
    
    if (!image) return null;
    
    return (
      <div className="hidden md:block popup-image-right">
        <img 
          src={image} 
          alt={alt_text || 'Popup Image'} 
          className="h-full w-full object-cover"
        />
      </div>
    );
  };

 
  const renderForm = () => {
    if (!formBlock) return null;
    
    const { 
      show_name_field,
      name_placeholder,
      email_placeholder,
      submit_button_text,
      button_style,
      phone_placeholder,
      sms_skip_text,
      sms_button_text
    } = formBlock.settings || {};
    
    return (
      <div className="popup-form mb-6">
        <form onSubmit={handleKlaviyoSubmit}>
        {
           currentStep == 'email' && ( 

            <>
            {show_name_field && (
              <div className="mb-4">
                <TextField
                  placeholder={name_placeholder || "Your First Name"}
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  type="floating-input"
                  required
                />
              </div>
            )}
            
            <div className="mb-4 min-h-[75px]">
              <TextField
                placeholder={email_placeholder || "Your Email"}
                label="Your Email"
                type="floating-input"
                name="email"
                value={formData.email}
                hasError={error != ''}
                errorMessage={error}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="mt-6 w-full">
              <Button
                type="submit"
                label={submit_button_text || "Get code"}
                style={button_style || "primary"}
                className="w-full h-[60px]"
                padding_horizontal={16}
              />
            </div>
            </>
           )
        }
        
        


          {
            currentStep == 'sms' && ( 
              <>
            <div className="mb-4">
              <TextField
                placeholder={phone_placeholder || "Your Phone Number (Optional)"}
                inputType="floating-input"
                name="phone"
                value={formData.phone}
                hasError={error != ''}
                errorMessage={error}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mt-6 flex flex-col">
  
              <Button
                type="submit"
                label={sms_button_text || "Submit"}
                style={button_style || "primary"}
                className="w-full h-[60px]"
                padding_horizontal={16}
              />
  
              <Button
                type="submit"
                label= {sms_skip_text || "Skip this step"}
                style={'light-link'}
                reverse
                icon={'right-arrow-alt'}
                onClick={handleSkipSms}
              />
              
            </div>
            </>
            )

          }
        </form>
      </div>
    );
  };


// Render disclaimer block
const renderDisclaimerBlock = () => {
  if (!disclaimerBlock) return null;

  const {
    disclaimer_text,
    discount_disclaimer_text,
    font_style,
    alignment
  } = disclaimerBlock.settings || {};

  const className = `popup-disclaimer ${font_style || ''} max-w-[294px] mx-auto text-${alignment || 'center'}`;

  return (
    <>
      {currentStep !== 'thank_you' && disclaimer_text && (
        <div className={className}>
          <div dangerouslySetInnerHTML={{ __html: disclaimer_text }} />
        </div>
      )}
      {currentStep === 'thank_you' && discount_disclaimer_text && (
        <div className={className}>
          <div dangerouslySetInnerHTML={{ __html: discount_disclaimer_text }} />
        </div>
      )}
    </>
  );
};

  // Render thank you block
  const renderThankYouBlock = () => {
    if (!thankYouBlock) return null;
    
    const { 
      discount_code,
      content_alignment,
    } = thankYouBlock.settings || {};
    
    return (
      <div className={`text-center w-full mb-6`}>        
    {discount_code && (
        <div
          className="default-input cursor-pointer flex items-center justify-center gap-2"
          onClick={() => copyToClipboard(discount_code)}
        >
        <div class="extended-text-field-wrap justify-between flex items-center h-[48px] p-3 border-[#AAA] border rounded-[12px] w-full"> 
          <div
            className="form-field discount-code-copy h1 cursor-pointer"
            tabIndex={0}
            aria-label="Discount code"
          >
            {discount_code}
          </div>
          <span className="icon icon-copy text-[24px] top-[14px] right-[12px]" />
        </div>
        </div>
      )}
      </div>
    );
  };

  // Render the appropriate step content
  const renderStepContent = () => {
    switch(currentStep) {
      case 'email':
        return (
          <>
            {renderHeaderBlock('email')}
            {renderForm()}
            {renderDisclaimerBlock()}
          </>
        );
      case 'sms':
        return (
          <>
            {renderHeaderBlock('sms')}
            {renderForm()}
          </>
        );
      case 'thank_you':
        return (
          <>
            {renderHeaderBlock('thank_you')}
            {renderThankYouBlock()}
            {renderDisclaimerBlock()}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`popup-container fixed inset-0 z-50 flex items-center justify-center ${isOpen ? 'block' : 'hidden'}`}
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div 
        className="popup-overlay fixed inset-0 bg-black transition-opacity"
        style={{ opacity: overlay_opacity || 0.5 }}
        onClick={closePopup}
      ></div>
      
      {/* Popup Content */}
      <div 
        ref={popupRef} 
        className={`${getPopupLayoutClasses()} ${getAnimationClasses()}`}
        style={getBackgroundStyle()}
      >
        {/* Close Button */}
        <button
          onClick={closePopup}
          className={`popup-close-btn absolute top-4 ${close_button_position === 'left' ? 'left-4' : 'right-4'} bg-black text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-800 focus:outline-none z-10`}
          aria-label="Close popup"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>

        {/* Mobile Image (top) */}
        {imageBlock && renderMobileImageBlock()}
        
        {/* Main popup layout (for desktop) */}
        <div className="md:flex">
          {/* Content wrapper */}
          <div className="popup-content-wrapper p-6 lg:px-[52px] sm:p-8 md:flex-1 my-auto">
            {renderStepContent()}
          </div>
          
          {/* Desktop Image (right) */}
          {imageBlock && renderDesktopImageBlock()}
        </div>
      </div>
    </div>
  );
}

export default Popup;